import { Asset } from '@/api/generatedApi';
import { AdvancedAnalysisButton } from '@/components/AdvancedAnalysisChat/AdvancedAnalysisButton';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/utils/featureFlags';
import { Box, styled, SxProps, Theme } from '@mui/material';
import { AddToCompareButton } from '../CompareCart/AddToCompareButton';
import { FindMatchesButton } from '../Matches/FindMatchesButton';
import { DeleteAssetButton } from './DeleteAssetButton';
import { DownloadAssetButton } from './DownloadAssetButton';
import { ReprocessAssetButton } from './ReprocessAssetButton';
import { ReprocessCriticalGeometryButton } from './ReprocessCriticalGeometryButton';
import { ViewAssetButton } from './ViewAssetButton';

const StyledAssetActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '2px',
  borderBottom: 'none',
  width: '100%',
  gap: theme.spacing(0.5),
}));

export const AssetActionRow = ({
  asset,
  hideAssetPageLink = false,
  showReprocess = false,
  onDelete,
  onReprocessCriticalGeometry,
  isReprocessCriticalGeometryDisabled = false,
  slim = false,
  sx,
}: {
  asset?: Asset;
  hideAssetPageLink?: boolean;
  showReprocess?: boolean;
  onDelete?: () => void;
  onReprocessCriticalGeometry?: () => void | Promise<void>;
  isReprocessCriticalGeometryDisabled?: boolean;
  slim?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const { currentUserIsAtleastTenantViewer } = useGetCurrentUser();
  const shouldShowActions = currentUserIsAtleastTenantViewer(asset?.tenantId);

  const iconStyles: SxProps<Theme> = slim
    ? {
        '& .MuiIconButton-root': {
          padding: '2px',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
        },
      }
    : {};

  return (
    shouldShowActions &&
    asset && (
      <StyledAssetActionRow data-testid={AssetPageObjectModel.AssetActionRow} sx={{ ...iconStyles, ...sx }}>
        {!hideAssetPageLink && <ViewAssetButton asset={asset} />}
        <FindMatchesButton asset={asset} variant='icon' />
        <AddToCompareButton asset={asset} variant='icon' />
        <ReprocessCriticalGeometryButton
          disabled={isReprocessCriticalGeometryDisabled}
          onReprocess={onReprocessCriticalGeometry}
        />
        {showReprocess && <ReprocessAssetButton asset={asset} />}
        <DownloadAssetButton asset={asset} variant='icon' />
        <DeleteAssetButton asset={asset} variant='icon' onDelete={onDelete} />
        {isFeatureEnabled(FEATURE_FLAGS.ADVANCED_ANALYSIS) && <AdvancedAnalysisButton assetId={asset.id} />}
      </StyledAssetActionRow>
    )
  );
};
